import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import React, { useState } from 'react';
import { ModalProps } from '../../../types/modal';
import { MRT_ColumnDef } from 'mantine-react-table';
import GenericTable from '../../../shared/GenericTable';
import BulkActionModal from './BulkActionModal';
import { User } from '../../../types/user';
import { capitalizeName } from '../../../../shared/utils';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';

interface Props extends ModalProps {
    user: User;
    upcomingReservations: any[];
}
const UserReservationsModal = ({
    user,
    upcomingReservations,
    open,
    onClose,
}: Props) => {
    const { firstName, lastName } = user;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [selected, setSelected] = useState<number[]>([]);

    const [modalOpen, setModalOpen] = useState(false);

    type Action = 'reassign' | 'cancel';
    const [bulkAction, setBulkAction] = useState<Action>();

    const handleAction = (action: Action) => {
        setBulkAction(action);
        setModalOpen(true);
    };

    const handleClose = () => onClose();

    const columns: MRT_ColumnDef<any>[] = [
        {
            accessorKey: 'id',
            header: 'Reservation Id',
            Cell: ({ cell }) => {
                return `#${cell.getValue()}`;
            },
        },
        {
            accessorKey: 'module.name',
            header: 'Experience',
        },
        {
            accessorKey: 'startDate',
            header: 'Check-in',
            Cell: ({ cell }) => {
                return new Date(cell.getValue<string>()).toLocaleDateString(
                    'en-AU',
                );
            },
        },
        {
            accessorKey: 'endDate',
            header: 'Checkout',
            Cell: ({ cell }) => {
                return new Date(cell.getValue<string>()).toLocaleDateString(
                    'en-AU',
                );
            },
        },
        {
            accessorKey: 'createdAt',
            header: 'Booked',
            Cell: ({ cell }) => {
                return new Date(cell.getValue<string>()).toLocaleDateString(
                    'en-AU',
                );
            },
        },
        {
            accessorKey: 'status',
            header: 'Status',
            Cell: ({ cell }) => {
                const value = cell.getValue<string>();
                return (
                    <CustomChip
                        variant='outlined'
                        label={capitalizeName(value)}
                        color={value === 'approved' ? 'yellow' : 'blue'}
                    />
                );
            },
        },
    ];

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth='xl'>
                <DialogTitle>
                    User Upcoming Reservations
                    <IconButton
                        onClick={handleClose}
                        sx={{ color: 'text.primary' }}
                        aria-label='close'
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    sx={{
                        width: '100%',
                        minWidth: { xs: '100%', md: '800px' },
                        px: { xs: 1, sm: 3 },
                    }}
                >
                    {upcomingReservations && !upcomingReservations.length ? (
                        <Typography sx={{ fontSize: 14 }}>
                            {firstName} {lastName} has no upcoming reservations
                            and can be disabled.
                        </Typography>
                    ) : (
                        <>
                            <Typography sx={{ fontSize: 14, mb: 2 }}>
                                {firstName} {lastName} is the primary guest of{' '}
                                {upcomingReservations.length} upcoming
                                reservations. Please cancel or reassign them
                                before disabling the user.
                            </Typography>
                            <GenericTable
                                columns={columns}
                                data={upcomingReservations ?? []}
                                tableName='Cancelled-Reservations'
                                loading={!upcomingReservations}
                                onSelectionChange={(selectedRows: any[]) => {
                                    setSelected(
                                        selectedRows.map((row) => row.id),
                                    );
                                }}
                                onRowClick={(params) =>
                                    window.open(
                                        `/reservations/experience/${params.id}`,
                                        '_blank',
                                    )
                                }
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ pl: 3 }}>
                    {upcomingReservations.length > 0 ? (
                        <Stack
                            direction={isMobile ? 'column' : 'row'}
                            justifyContent='space-between'
                            alignItems='center'
                            flexWrap='nowrap'
                            sx={{ width: '100%' }}
                        >
                            <Box>
                                <Typography>
                                    {selected.length} reservation
                                    {selected.length !== 1 ? 's' : ''} selected
                                </Typography>
                            </Box>
                            <Stack
                                justifyContent='space-between'
                                gap={1}
                                direction={isMobile ? 'column' : 'row'}
                            >
                                <Button
                                    variant='contained'
                                    onClick={() => handleAction('reassign')}
                                    disabled={selected.length === 0}
                                >
                                    Reassign
                                </Button>
                                <Button
                                    variant='contained'
                                    color='error'
                                    onClick={() => handleAction('cancel')}
                                    disabled={selected.length === 0}
                                >
                                    Cancel Reservation
                                    {selected.length === 1 ? '' : 's'}
                                </Button>
                                <Button
                                    variant='contained'
                                    color='secondary'
                                    onClick={onClose}
                                >
                                    Close
                                </Button>
                            </Stack>
                        </Stack>
                    ) : (
                        <Button
                            variant='contained'
                            color='secondary'
                            onClick={onClose}
                        >
                            Close
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            <BulkActionModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                user={user}
                ids={selected}
                action={bulkAction}
            />
        </>
    );
};

export default UserReservationsModal;
